import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Box, Button, Paper, Typography } from '@mui/material';
import { StyledTable } from '../index';
import { useTorqueColumns } from '../TorqueTable/useTorqueColumns';
import useMachineData from '../../hooks/useMachineData';
import ReLogin from '../ReLogin/ReLogin';
import ReasonAndRemarkModal from '../ReportModal/ReasonAndRemarkModal';
import { privilegedSkip } from '../../redux/actionCreators/processValidation';
import { DARK_MODE, isPuneJlr } from '../../constants';
import { toast } from 'react-toastify';

/**
 * Embedded machine-data panel shown inside station-op screens (WAHA, BA, RBT …)
 * when the station's allowedOperations includes MACHINE.
 *
 * Props:
 *   resolvedSerialNumber  – active VIN
 *   sequenceNumber        – current line-item sequenceNumber
 *   onMachineStatusChange – callback(isComplete: boolean) so parent can gate Mark Complete
 *   themeMode             – Redux theme string
 *   privilegedSkip        – Redux-bound action
 */
const MachineDataSection = ({
  resolvedSerialNumber,
  sequenceNumber,
  onMachineStatusChange,
  themeMode,
  privilegedSkip,
}) => {
  const isDark = themeMode === DARK_MODE;

  const {
    torqueTableRows,
    selectedTorqueRows,
    handleTorqueSelectionChange,
    isMachineDataComplete,
    callSecondApi,
    features,
  } = useMachineData(resolvedSerialNumber);

  const isDisplayOverallParamsFalse =
    features.find((f) => f.name === 'DISPLAY_OVERALL_PARAMS')?.enabled === false;
  const torqueTableHeader = useTorqueColumns(isDisplayOverallParamsFalse);

  const [showReLoginModal, setShowReLoginModal] = useState(false);
  const [showSkipRemarksModal, setShowSkipRemarksModal] = useState(false);
  const [skipReasonData, setSkipReasonData] = useState({ reason: '', remarks: '' });

  // Notify parent whenever machine completeness changes
  useEffect(() => {
    onMachineStatusChange?.(isMachineDataComplete);
  }, [isMachineDataComplete, onMachineStatusChange]);

  const handleFailForward = () => {
    if (selectedTorqueRows.length === 0) {
      toast.warning('Please select a row to skip');
      return;
    }
    setShowSkipRemarksModal(true);
  };

  const handleSkipMachineOperation = (reason, remarks) => {
    setSkipReasonData({ reason, remarks });
    setShowSkipRemarksModal(false);
    setShowReLoginModal(true);
  };

  const handleReLoginSuccess = async (e, username, password) => {
    e.preventDefault();
    try {
      const privilegedRequest = {
        username,
        password,
        action: 'GROUP_LEADER',
        remark: skipReasonData.remarks,
      };

      const rowsToProcess = torqueTableRows.filter((row) =>
        selectedTorqueRows.includes(row.id)
      );

      if (rowsToProcess.length === 0) {
        setShowReLoginModal(false);
        toast.warning('Please select a row to skip');
        return;
      }

      const machineGroups = rowsToProcess.reduce((acc, row) => {
        if (!row.machineId) return acc;
        if (!acc[row.machineId]) acc[row.machineId] = [];

        const qty = row.quantity || 1;
        const executionStatuses = row.executionStatuses || Array.from({ length: qty }, () => null);
        const batchRawStatuses = row.batchRawStatuses || Array.from({ length: qty }, () => {
          if (isPuneJlr()) {
            const fallback = row.paramStatus || row.status;
            return (fallback && fallback.toUpperCase() !== 'PENDING') ? fallback : 'PENDING';
          }
          return 'PENDING';
        });
        const batchActualNumbers = row.batchActualNumbers || Array.from({ length: qty }, (_, i) => i + 1);

        for (let i = 0; i < qty; i++) {
          if (executionStatuses[i] !== true) {
            const batchNum = batchActualNumbers[i];
            let finalStatus = isDisplayOverallParamsFalse ? 'NOK' : batchRawStatuses[i];
            if (isPuneJlr()) {
              const currentStatus = batchRawStatuses[i];
              if (!currentStatus || currentStatus.toUpperCase() === 'PENDING') {
                const fallback = row.paramStatus || row.status;
                if (fallback && fallback.toUpperCase() !== 'PENDING') {
                  finalStatus = fallback;
                } else {
                  finalStatus = 'PENDING';
                }
              } else {
                finalStatus = currentStatus;
              }
            }
            acc[row.machineId].push({
              parameterName: row.parameterKey || row.parameterName || '',
              parameterDescription:
                row.parameterDescription || row.parameterName || row.parameterKey || '',
              status: finalStatus,
              unit: row.unit || '',
              value: row.value || '',
              batchNumber: isPuneJlr() ? (row.batchNumber || batchNum) : batchNum,
              reason: skipReasonData.reason,
              remark: skipReasonData.remarks,
            });
          }
        }

        return acc;
      }, {});

      const parametersSkipRequest = Object.keys(machineGroups)
        .map((machineId) => ({
          machineId: parseInt(machineId),
          processesToSkip: machineGroups[machineId],
        }))
        .filter((group) => group.processesToSkip.length > 0);

      if (parametersSkipRequest.length === 0) {
        setShowReLoginModal(false);
        toast.info('All batches for this parameter are already OK');
        return;
      }

      privilegedSkip(
        sequenceNumber,
        { partSkipRequest: null, parametersSkipRequest, privilegedRequest },
        () => {
          setShowReLoginModal(false);
          callSecondApi();
          handleTorqueSelectionChange([]);
          toast.success('Parameter(s) skipped successfully');
        },
        (error) => {
          setShowReLoginModal(false);
          toast.error(error?.response?.data?.message || 'Failed to skip machine operation');
        }
      );
    } catch (err) {
      setShowReLoginModal(false);
      toast.error(err.message);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Paper>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, pb: 1 }}>
          <Typography variant="h6" sx={{ color: isDark ? '#fff' : '#000' }}>
            Machine Data
          </Typography>
        </Box>
        <StyledTable
          columns={torqueTableHeader}
          rows={torqueTableRows}
          selectall={true}
          elevated
          paginated={true}
          showCheckBox={true}
          selectedRows={selectedTorqueRows}
          onSelectionChange={handleTorqueSelectionChange}
          noDataText="No Machine Data Available"
        />
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
        <Button
          variant="contained"
          color="warning"
          onClick={handleFailForward}
          disabled={selectedTorqueRows.length === 0}
          sx={{ px: 4, borderRadius: '12px', height: 48 }}
        >
          Fail Forward
        </Button>
      </Box>

      <ReLogin
        open={showReLoginModal}
        handleReLogin={handleReLoginSuccess}
        handleClose={() => setShowReLoginModal(false)}
        isDark={isDark}
      />

      {showSkipRemarksModal && (
        <ReasonAndRemarkModal
          nameofModal="Skip Machine Operation"
          open={showSkipRemarksModal}
          handleClose={() => setShowSkipRemarksModal(false)}
          handleSubmit={handleSkipMachineOperation}
          data={{ vin: resolvedSerialNumber, partName: 'Machine Operation' }}
          dropDown={[
            'MACHINE ERROR',
            'PARAMETER NOT OK',
            'NO RESPONSE FROM MACHINE',
            'MANUAL SKIP REQUIRED',
            'OTHER',
          ]}
          leftTitle="VIN"
          rightTitle="Operation"
        />
      )}
    </Box>
  );
};

const mapStateToProps = (state) => ({
  themeMode: state.theme.themeMode,
});

const mapDispatchToProps = (dispatch) => ({
  privilegedSkip: (asn, payload, success, error) =>
    dispatch(privilegedSkip(asn, payload, success, error)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MachineDataSection);
